import { Injectable, inject } from "@angular/core";
import {
  collection,
  doc,
  docData,
  DocumentReference,
  Firestore,
  getDocs,
  onSnapshot,
  query,
  where,
} from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, of } from "rxjs";
import { catchError, map, shareReplay, switchMap } from "rxjs/operators";
import { CommonVariable } from "src/app/app.constants";
import { CommonService } from "src/app/services/common/common.service";
import { StoreJackpotRepositoryService } from "../store-jackpot.service";
import { StoreFirestoreHelperService } from "./store-firestore-helper.service";

/**
 * Dedicated data service for jackpot-trigger domain that was previously mixed
 * inside `StoreRepositoryService`. Owns the trigger observables, the trigger
 * list, alias resolution helpers and the activated-count subject.
 */
@Injectable({
  providedIn: "root",
})
export class StoreJackpotTriggerDataService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private commonService = inject(CommonService);
  private storeJackpotService = inject(StoreJackpotRepositoryService);
  private storeFirestoreHelper = inject(StoreFirestoreHelperService);

  preferredCurrency: string = CommonVariable.preferredCurrency;
  jackpotActivatedCount$ = new BehaviorSubject<number>(0);

  private ensureAuthenticated(): Observable<boolean> {
    return this.authService.ensureValidAuth();
  }

  jackpotTriggerCard$ = this.storeFirestoreHelper.getAngularFireStore(`/jackpot_trigger`);

  // This function is used to get jackpot trigger orders from firebase
  jackpotTriggerOrder$ = this.ensureAuthenticated().pipe(
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        this.authService.errorHandlerLogs("User not authenticated for jackpot trigger order");
        return of([]);
      }
      return docData(doc(this.firestore, "Global_lookup/jackpot_trigger_order")).pipe(
        switchMap((data: any) => {
          return this.authService.userDetails$.pipe(
            map((user: any) => {
              const userCountryCode = user?.country_code || CommonVariable.countryCode;
              const userData = data?.jackpot_triggers?.[userCountryCode] || data?.jackpot_triggers?.["other"] || [];
              return userData;
            })
          );
        }),
        shareReplay(1),
        catchError((error) => {
          this.authService.errorHandlerLogs("Error loading jackpot trigger order:", error);
          return of([]);
        })
      );
    })
  );

  jackpotTrigger$ = combineLatest([
    this.jackpotTriggerCard$,
    this.storeJackpotService.userJackpotTrigger$,
    this.storeJackpotService.jackpots$,
    this.jackpotTriggerOrder$,
  ]).pipe(
    switchMap(([r1, r2, r3, r4]: [Data[], Data[], Data[], Data[]]) => {
      // Add null checks for all inputs from combineLatest
      if (!r1 || !Array.isArray(r1)) {
        this.authService.errorHandlerLogs("jackpotTriggerCard$ returned null or invalid data");
        return of({
          inactiveJackpots: [],
          activeJackpots: [],
          userJackpots: [],
          totalJackpots: 0,
          jackpotTriggerOrder: [],
        });
      }

      if (!r3 || !Array.isArray(r3)) {
        this.authService.errorHandlerLogs("jackpots$ returned null or invalid data");
        return of({
          inactiveJackpots: [],
          activeJackpots: [],
          userJackpots: [],
          totalJackpots: 0,
          jackpotTriggerOrder: [],
        });
      }

      const filteredUserTriggers = Array.isArray(r2) ? r2.filter((data) => data !== null) : [];
      const jackpotsPromises = r1.map(async (obj: Data) => {
        if (!obj) return null;

        const data: Data = { ...obj };

        const findUserJackpotData = filteredUserTriggers?.find((x) => x?.id === obj?.id);
        const jackpotValue = r3?.find(
          (val) => val?.real_name?.toLowerCase() === obj?.jackpot_name?.toLowerCase()
        );

        data.status = findUserJackpotData?.status ?? false;
        data.trigger_active_count = findUserJackpotData?.trigger_active_count ?? 0;
        data.sql_trigger_id = findUserJackpotData?.sql_trigger_id ?? "";
        // Prefer user-subscription ids when present so deactivate/terminate can persist.
        data.offer_link_id =
          findUserJackpotData?.offer_link_id ??
          findUserJackpotData?.prosub_id ??
          obj?.offer_link_id;
        data.offer_id = findUserJackpotData?.offer_id ?? obj?.offer_id;
        data.free_jackpot =
          findUserJackpotData?.free_jackpot ?? obj?.free_jackpot ?? false;
        data.alternate_free_jackpot =
          findUserJackpotData?.alternate_free_jackpot ??
          obj?.alternate_free_jackpot ??
          false;
        data.free_offer_id =
          findUserJackpotData?.free_offer_id ?? obj?.free_offer_id ?? 0;
        data.jackpot_value = jackpotValue?.int_jackpot_value?.[this.preferredCurrency]
          ?? jackpotValue?.jackpot_value;
        data.jackpot = findUserJackpotData?.preferred_currency?.jackpot_trigger;
        data.background = jackpotValue?.background;

        const currentJackpotRef = findUserJackpotData?.current_jackpot as DocumentReference | undefined;

        if (currentJackpotRef) {
          try {
            const snapshot = await new Promise<Data>((resolve) => {
              const unsubscribe = onSnapshot(
                currentJackpotRef,
                (doc) => {
                  unsubscribe(); // Unsubscribe after first snapshot if you don't need real-time
                  resolve(doc);
                },
                (error) => {
                  this.authService.errorHandlerLogs("❌ Firestore snapshot error:", error);
                  unsubscribe();
                  // Resolve with null instead of rejecting to prevent Promise.all() failure
                  resolve(null);
                }
              );
            });

            const jackpotData = snapshot?.data();
            data.current_jackpot =
              jackpotData?.int_jackpot_value?.[this.preferredCurrency] ??
              jackpotData?.jackpot_value;
          } catch (e) {
            this.authService.errorHandlerLogs("Error fetching current jackpot:", e);
            // Continue processing rather than returning error
          }
        }

        const thresholdValue = obj?.int_jackpot?.[this.preferredCurrency] ?? obj?.jackpot;
        const jackpotNumeric = Number(data?.jackpot_value?.replace(/[^0-9]/g, ""));
        const thresholdNumeric = Number(thresholdValue?.replace(/[^0-9]/g, ""));
        data.isReachedThreshold = jackpotNumeric >= thresholdNumeric;

        return data;
      });

      return from(Promise.all(jackpotsPromises)).pipe(
        map((jackpots: Data[]) => {
          // Filter out null values that might have been returned
          const validJackpots = jackpots.filter((j) => j !== null);

          const inactiveJackpots = validJackpots.filter((j) => !j.status);
          const activeJackpots = validJackpots.filter((j) => j.status);
          const totalJackpots = activeJackpots.reduce((acc, cur) => acc + (cur.trigger_active_count || 0), 0);

          this.jackpotActivatedCount$.next(totalJackpots);

          return {
            inactiveJackpots,
            activeJackpots,
            userJackpots: validJackpots,
            totalJackpots,
            jackpotTriggerOrder: Array.isArray(r4) ? r4 : [],
          };
        }),
        catchError((error) => {
          this.authService.errorHandlerLogs("❌ Error in Promise.all() for jackpot triggers:", error);
          return of({
            inactiveJackpots: [],
            activeJackpots: [],
            userJackpots: [],
            totalJackpots: 0,
            jackpotTriggerOrder: Array.isArray(r4) ? r4 : [],
          });
        })
      );
    }),
    catchError((error) => {
      this.authService.errorHandlerLogs("❌ Error in jackpotTrigger$ stream:", error);
      return of({
        inactiveJackpots: [],
        activeJackpots: [],
        userJackpots: [],
        totalJackpots: 0,
        jackpotTriggerOrder: [],
      });
    }),
    shareReplay(1)
  );

  triggerList$: BehaviorSubject<Array<Data>> = new BehaviorSubject<Array<Data>>([]);

  jackpotTriggers$ = combineLatest(
    this.jackpotTriggerCard$,
    this.triggerList$,
    this.storeJackpotService.jackpots$,
    this.jackpotTriggerOrder$
  ).pipe(
    map(([val1, val2, val3, val4]: Array<Data>) => {
      try {
        const finalTriggerList = val2?.map((value) => {
          const triggerDetails = val1?.find((element) => element?.alias == value?.alias);
          if (triggerDetails) {
            value = { ...triggerDetails, ...value };
            const jackpotDetails = val3?.find(
              (val: Data) => val?.real_name?.toLowerCase() === value?.jackpot_name?.toLowerCase()
            );
            if (jackpotDetails) {
              value.jackpot_value = jackpotDetails?.int_jackpot_value?.[this.preferredCurrency]
                ? jackpotDetails?.int_jackpot_value?.[this.preferredCurrency]
                : jackpotDetails?.jackpot_value;
              value.background = jackpotDetails?.background;
            }
            const aliasSplit = value?.alias?.split("-") ?? [];
            value.storeType = aliasSplit[aliasSplit?.length - 1];

            const regex = /\[currency_beautify:(\d+[MK]?)\]/;
            const currentJackpotValue = value?.int_jackpot[this.preferredCurrency];
            const jackpotTriggerValue = value?.content?.jackpot_trigger;
            const jackpotTriggerDescription = value?.content?.description;
            value.content.jackpot_trigger = jackpotTriggerValue?.replace(regex, currentJackpotValue);
            value.content.description = jackpotTriggerDescription?.replace(regex, currentJackpotValue);
            return value;
          }
        });

        if (val4 && val4?.length > 0) {
          // We are sorting the trigger list based on this collection "/Global_lookup/jackpot_trigger_order"
          const resultArray = [];
          const remainingItems = [];
          const orderMap = new Map(val4?.map((item, index) => [item, index]));
          finalTriggerList?.forEach((element) => {
            if (orderMap?.has(element?.jackpot_name)) {
              resultArray[orderMap.get(element?.jackpot_name) as number] = element;
            } else {
              remainingItems.push(element);
            }
          });

          // Remove undefined slots (if any) and combine the arrays
          const sortedTriggerList = resultArray.filter((item) => item !== undefined).concat(remainingItems);
          return sortedTriggerList;
        } else {
          return finalTriggerList;
        }
      } catch (error) {
        return [];
      }
    })
  );

  constructor() {
    this.authService.userDetails$?.subscribe((user: any) => {
      this.preferredCurrency = user?.preferred_currency ?? CommonVariable.preferredCurrency;
    });
  }

  /**
   * The function `getJackpotTriggers` retrieves and filters deactive jackpot triggers for a specific
   * user from local storage.
   */
  getJackpotTriggers() {
    this.commonService.getUid("").pipe(
      switchMap((userId) => {
        if (!userId) {
          this.triggerList$.next([]);
          return of([]); // emit empty array
        }
        const triggerDocRef = doc(this.firestore, `users/${userId}/jackpot_trigger/trigger_list`);
        return docData(triggerDocRef).pipe(
          map((data: any) => {
            try {
              const triggerDetails = data?.triggers;
              const deActiveKeys = Object.keys(triggerDetails?.deactive ?? {});
              const deActiveList = deActiveKeys.map((key: string) => triggerDetails.deactive[key]);
              const triggersArray = [...deActiveList];
              this.triggerList$.next(triggersArray);

              return triggersArray;
            } catch (error) {
              this.triggerList$.next([]);
              return [];
            }
          })
        );
      }),
      catchError((err) => {
        this.triggerList$.next([]);
        return of([]);
      }),
      shareReplay(1)
    ).subscribe();
  }

  async getAliasName(aliasKey: string): Promise<string> {
    const queryMap: { [key: string]: [string, string] } = {
      "lotto-std": ["lotto-free", "lotto-std"],
      "euro-std": ["euro-free", "euro-std"],
      "pb-std": ["pb-free", "pb-std"],
      "lprimitiva-std": ["lprimitiva-free", "lprimitiva-std"],
      "euroj-std": ["euroj-free", "euroj-std"],
      "superena-std": ["superena-free", "superena-std"],
      "flotto-std": ["flotto-free", "flotto-std"],
      "lmax-std": ["lmax-free", "lmax-std"],
    };

    let jackpotTrigger = aliasKey;
    if (queryMap[aliasKey]) {
      const [collection, alias] = queryMap[aliasKey];
      jackpotTrigger = await this.queryFirestore(collection, alias);
    }
    return jackpotTrigger;
  }

  private queryFirestore(alias: string, triggerValue: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.commonService.getUid("StoreRepositoryService").subscribe({
        next: async (userId) => {
          try {
            if (!userId) throw new Error("User ID is missing");

            const collectionRef = collection(this.firestore, `/users/${userId}/jackpot_trigger`);
            const firestoreQuery = query(collectionRef, where("alias", "==", alias));
            const querySnapshot = await getDocs(firestoreQuery);

            const result = querySnapshot.docs?.length > 0 ? triggerValue : alias;
            resolve(result); // ✅ return value properly
          } catch (error) {
            reject(error);
          }
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  async queryFirestores(): Promise<any[]> {
    return await firstValueFrom(
      this.authService.getUid$("StoreRepositoryService").pipe(
        switchMap((userId) => {
          if (!userId) {
            return of([]); // Return empty array if no userId
          }

          const collectionRef = collection(this.firestore, `/users/${userId}/jackpot_trigger`);

          const snapshotChanges = new Observable((observer) => {
            const unsubscribe = onSnapshot(
              collectionRef,
              (snapshot) => {
                const actions = snapshot.docs.map((doc) => ({
                  payload: { doc }, // Mimic snapshotChanges() structure
                  type: "added",
                }));
                observer.next(actions); // Emit data
              },
              (error) => observer.error(error)
            );
            return () => unsubscribe(); // Teardown
          }).pipe(
            map((actions: any) => {
              return actions.map((a) => a.payload.doc.data());
            }),
            switchMap((dataArray: any[]) => {
              const aliases = dataArray.filter((doc) => doc.alias);
              return of(aliases); // Return only documents with alias
            })
          );

          return snapshotChanges; // Return observable to pipe
        })
      )
    );
  }
}
